import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import { stateCityData } from './searchstatedata';
import { doctorSpecializations } from './doctorSpecializations';
import './SearchDoctor.css';
import { Box } from '@mui/material';

const SearchDoctor = () => {
  const { patientUser } = useContext(AuthContext);
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  const [specialization, setSpecialization] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSearchType, setActiveSearchType] = useState(null);
  const [platformFeeRate, setPlatformFeeRate] = useState(0.2);
  const navigate = useNavigate();

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const FALLBACK_IMAGE = 'https://placehold.co/150x150';

  useEffect(() => {
    const fetchPlatformSettings = async () => {
      if (!patientUser || !patientUser.accessToken) {
        setError("Authentication required to fetch platform settings.");
        setPlatformFeeRate(0.2);
        return;
      }
      try {
        const response = await api.get('/appointments/platform-settings', {
          headers: { Authorization: `Bearer ${patientUser.accessToken}` },
        });
        setPlatformFeeRate(response.data.patientCommission / 100);
        setError('');
      } catch (err) {
        console.error('Failed to fetch platform settings:', err.response || err);
        setError('Failed to fetch platform settings. Using default fee.');
        setPlatformFeeRate(0.2);
      }
    };
    fetchPlatformSettings();
  }, [patientUser]);

  const handleSearchTypeSelect = (type) => {
    setActiveSearchType(activeSearchType === type ? null : type);
    setState('');
    setCity('');
    setSpecialization('');
    setDoctorName('');
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setState(selectedState);
    const selectedStateData = stateCityData.find((data) => data.state === selectedState);
    setCities(selectedStateData ? selectedStateData.cities : []);
    setCity('');
  };

  const handleSearch = async () => {
    if (!activeSearchType) {
      setError('Please select a search category.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = { isVerified: true };
      if (activeSearchType === 'location') {
        if (!state || !city) {
          setError('Please select both state and city.');
          setLoading(false);
          return;
        }
        params.state = state;
        params.city = city;
      } else if (activeSearchType === 'specialty') {
        if (!specialization) {
          setError('Please select a specialty.');
          setLoading(false);
          return;
        }
        params.specialization = specialization;
      } else if (activeSearchType === 'name') {
        if (doctorName) params.name = doctorName;
      }

      console.log('Sending request to /doctors/search with params:', params);
      const response = await api.get('/doctors/search', { params });
      console.log('Raw API response.data:', response.data);

      if (!Array.isArray(response.data)) {
        throw new Error('API response is not an array');
      }

      const doctorsWithAvailability = response.data.map(doctor => {
        const availability = doctor.availability && typeof doctor.availability === 'object'
          ? { ...doctor.availability }
          : daysOfWeek.reduce((acc, day) => ({ ...acc, [day]: [] }), {});
        console.log(`Processed availability for ${doctor.name}:`, availability);
        return { ...doctor, availability };
      });

      setDoctors(doctorsWithAvailability);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to find doctors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalFee = (fee) => {
    const consultationFee = parseInt(fee) || 0;
    return consultationFee + (consultationFee * platformFeeRate);
  };

  const handleBookAppointment = (doctorId) => {
    const doctor = doctors.find((doc) => doc._id === doctorId);
    navigate('/book-appointment', {
      state: { doctorId: doctor._id, state: doctor.state, city: doctor.city, platformFeeRate },
    });
  };

  const hasAvailability = (availability) => {
    console.log('Checking availability:', availability);
    if (!availability || Object.keys(availability).length === 0) return false;
    return daysOfWeek.some(day => {
      const slots = availability[day];
      const hasSlots = Array.isArray(slots) && slots.length > 0;
      console.log(`Day ${day}:`, { slots, hasSlots });
      return hasSlots;
    });
  };

  return (
    <div className="doctor-search-page">
      <header className="search-header">
        <h1>Find Your Doctor</h1>
        <p>Search by location, specialty, or name</p>
      </header>

      <section className="search-panel">
        <div className="search-tabs">
          <button className={`tab-btn ${activeSearchType === 'location' ? 'active' : ''}`} onClick={() => handleSearchTypeSelect('location')}>
            Location
          </button>
          <button className={`tab-btn ${activeSearchType === 'specialty' ? 'active' : ''}`} onClick={() => handleSearchTypeSelect('specialty')}>
            Specialty
          </button>
          <button className={`tab-btn ${activeSearchType === 'name' ? 'active' : ''}`} onClick={() => handleSearchTypeSelect('name')}>
            Doctor Name
          </button>
        </div>

        <div className="search-inputs">
          {activeSearchType === 'location' && (
            <div className="input-row">
              <div className="input-field">
                <label>State</label>
                <select value={state} onChange={handleStateChange}>
                  <option value="">Select State</option>
                  {stateCityData.map((data) => (
                    <option key={data.state} value={data.state}>{data.state}</option>
                  ))}
                </select>
              </div>
              <div className="input-field">
                <label>City</label>
                <select value={city} onChange={(e) => setCity(e.target.value)} disabled={!state}>
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {activeSearchType === 'specialty' && (
            <div className="input-row">
              <div className="input-field">
                <label>Specialty</label>
                <select value={specialization} onChange={(e) => setSpecialization(e.target.value)}>
                  <option value="">Select Specialty</option>
                  {doctorSpecializations.map((spec) => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {activeSearchType === 'name' && (
            <div className="input-row">
              <div className="input-field">
                <label>Doctor Name</label>
                <input
                  type="text"
                  placeholder="Enter doctor's name"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                />
              </div>
            </div>
          )}

          <button className="search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Find Doctors'}
          </button>
        </div>
      </section>

      {error && <div className="error-alert">{error}</div>}

      <section className="results-container">
        {doctors.length > 0 ? (
          doctors.map((doctor) => {
            const availability = doctor.availability || {};
            const availableDays = daysOfWeek.filter(
              day => availability[day] && Array.isArray(availability[day]) && availability[day].length > 0
            );
            console.log(`Rendering ${doctor.name} with availability:`, availability);
            console.log(`Available days for ${doctor.name}:`, availableDays);

            return (
              <div key={doctor._id} className="doctor-tile">
                <div className="doctor-info">
                  <Box className="doctor-card-header">
                    <div className="profile-pic-container">
                      <img 
                        src={doctor.profilePicture} 
                        alt={doctor.name} 
                        className="doctor-profile-pic"
                        onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                      />
                    </div>
                    <h3>{doctor.name} <span className="verified-badge">✔</span></h3>
                  </Box>
                  <div className="doctor-details">
                    <p><span>Specialty:</span> {doctor.specialization}</p>
                    <p><span>Experience:</span> {doctor.experience} years</p>
                    <p><span>Location:</span> {doctor.city}, {doctor.state}</p>
                    <p><span>Fee:</span> ₹{calculateTotalFee(doctor.consultationFee).toFixed(2)}</p>
                    <p><span>Rating:</span> {doctor.averageRating || 0} / 5 ★</p>
                  </div>
                  <p><span>Availability:</span></p>
                  <div className="availability-table">
                    {hasAvailability(availability) ? (
                      availableDays.map((day) => (
                        <div key={day} className="availability-block">
                          <span className="day-label">{day}</span>
                          <div className="slots-container">
                            {availability[day].map((slot, index) => (
                              <span key={index} className="time-slot">{slot}</span>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <span className="no-availability">No availability set</span>
                    )}
                  </div>
                </div>
                <button className="book-btn" onClick={() => handleBookAppointment(doctor._id)}>
                  Book Now
                </button>
              </div>
            );
          })
        ) : (
          <p className="no-results">No verified doctors found. Try adjusting your search.</p>
        )}
      </section>
    </div>
  );
};

export default SearchDoctor;